<template>
  <CommonPageView
    class="tws-news-view"
    :is-grey-background="false"
    :breadcrumb-items="breadcrumbItems"
    :loading="loading"
    :banner="banner">
    <template #content>
      <div class="row date-fields-container">
        <div class="col-md-3 col-sm-12">
          <tws-date-picker
            :value="dateFrom"
            :label="$t('news.archive.date-from')"
            @input="onDateInput('from', $event)"
            @keydown.native="onDateKeydown" />
          <div
            v-if="dateFromError"
            class="field-validation-errors">
            <p class="input-invalid-text">
              {{ dateFromError }}
            </p>
          </div>
        </div>
        <div class="col-md-3 col-sm-12">
          <tws-date-picker
            :value="dateTo"
            :label="$t('news.archive.date-to')"
            @input="onDateInput('to', $event)"
            @keydown.native="onDateKeydown" />
          <div
            v-if="dateToError"
            class="field-validation-errors">
            <p class="input-invalid-text">
              {{ dateToError }}
            </p>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-9 col-sm-12">
          <tws-loading-information
            class="loading-information"
            :loading="loading"
            :exception="exception"
            :exception-text="$t('news.archive.loading-failed')" />
          <tws-dashboard-informational-news
            :news="news"
            :link="'/news-page'"
            :title="$t('news.latest-news')" />
          <tws-pagination
            :current-page="currentPage"
            :extra-page-count="extraPageCount"
            :data-count="totalCount"
            :item-count-on-page="itemCountOnPage"
            class="pagination"
            @onPageClick="onPageClick" />
        </div>
      </div>
    </template>
  </CommonPageView>
</template>

<script>
import {
  mapActions,
  mapState
} from 'vuex'

import CommonPageView from '@/views/CommonPageView'
import InformationalNews from '@/views/login-landing/dashboard/dashboard-informational-news'
import {
  TwsDatePicker,
  TwsPagination,
  TwsLoadingInformation
} from 'tws-vue-components'

import i18n from '@/i18n'
import { BREADCRUMBS_NEWS_ARCHIVE } from '@/constants/breadcrumbs-constants'
import { isLoggedIn } from '@/utils/user.utils'

export default {
  components: {
    CommonPageView,
    'tws-dashboard-informational-news': InformationalNews,
    TwsDatePicker,
    TwsPagination,
    TwsLoadingInformation
  },
  data () {
    return {
      dateFrom: '',
      dateTo: '',
      dateFromError: '',
      dateToError: '',
      currentPage: 1,
      extraPageCount: 3,
      itemCountOnPage: 10,
      breadcrumbItems: BREADCRUMBS_NEWS_ARCHIVE
    }
  },
  computed: {
    ...mapState('news', [
      'loading',
      'exception',
      'news',
      'totalCount'
    ]),
    banner () {
      return {
        heading: i18n.t('dashboard-container.operational-news.action-button.news-archive')
      }
    }
  },
  mounted () {
    this.loadNews()
  },
  methods: {
    ...mapActions('news', [
      'loadPublicNews',
      'loadAllNews'
    ]),

    onDateKeydown (e) {
      const allowedKeys = [
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'Tab',
        '-'
      ]
      if (!/^\d$/.test(e.key) && !allowedKeys.includes(e.key)) {
        e.preventDefault()
      }
    },

    onDateInput (field, val) {
      let cleaned = val.replace(/[^\d-]/g, '')
      const digitsOnly = cleaned.replace(/\D/g, '')

      if (digitsOnly.length === 8 && !cleaned.includes('-')) {
        cleaned = `${digitsOnly.slice(0, 4)}-${digitsOnly.slice(4, 6)}-${digitsOnly.slice(6)}`
      }

      if (field === 'from') {
        this.dateFrom = cleaned
      } else {
        this.dateTo = cleaned
      }

      this.onDateChange()
    },

    onDateChange () {
      this.dateFromError = ''
      this.dateToError = ''

      const isDateFromValid = this.validateFullDate(this.dateFrom)
      const isDateToValid = this.validateFullDate(this.dateTo)

      if (!isDateFromValid.valid) {
        this.dateFromError = isDateFromValid.message
      }
      if (!isDateToValid.valid) {
        this.dateToError = isDateToValid.message
      }

      if (isDateFromValid.valid && isDateToValid.valid) {
        this.loadNews()
      }
    },

    validateFullDate (dateStr) {
      if (!dateStr || dateStr === '') { return { valid: true } }

      const regex = /^\d{4}-\d{2}-\d{2}$/
      if (!regex.test(dateStr)) {
        return {
          valid: false,
          message: this.$t('news.archive.date-invalid')
        }
      }

      const [
        year,
        month,
        day
      ] = dateStr.split('-').map(Number)
      const date = new Date(year, month - 1, day)

      if (
        date.getFullYear() !== year ||
    date.getMonth() + 1 !== month ||
    date.getDate() !== day
      ) {
        return {
          valid: false,
          message: this.$t('news.archive.date-invalid')
        }
      }

      return { valid: true }
    },

    loadNews () {
      const form = {
        dateFrom: this.dateFrom || null,
        dateTo: this.dateTo || null,
        page: this.currentPage,
        size: this.itemCountOnPage
      }

      isLoggedIn() ? this.loadAllNews(form) : this.loadPublicNews(form)
    },

    onPageClick (page) {
      this.currentPage = page
      this.loadNews()
    }
  }
}
</script>

<style lang="scss" scoped>
@import "tws-core-atoms/variables.scss";

.pagination {
  margin-top: 35px
}

.loading-information {
  padding: 20px 0px
}
</style>
